export const fullProgramTranslations = {
  en: {
    hero: {
      badge: 'Complete Course',
      title: 'Full Program',
      subtitle: 'Master AI-powered development from zero to product launch'
    },
    pricing: {
      originalPrice: '$1200',
      currentPrice: '$900',
      discount: 'Save $300',
      button: 'Proceed to Payment',
      secure: '🔒 Secure payment via Stripe & monobank',
      noAccount: 'No account required',
      payWith: 'Pay by card (Visa/Mastercard) or monobank',
      currency: 'Currency conversion is shown during checkout.'
    },
    content: {
      badge: '🎓 Complete Learning Path',
      description: 'Get unlimited access to all modules, exclusive content, and personalized mentorship. The complete path from beginner to confident AI product builder.'
    },
    features: {
      title: 'What\'s Included',
      items: [
        {
          title: 'All Modules Unlocked',
          description: 'Complete access to every module in the program, including future additions'
        },
        {
          title: 'Personal Mentorship',
          description: '1:1 sessions with experienced mentors to guide your learning journey'
        },
        {
          title: 'Real Projects',
          description: 'Build actual products that you can showcase in your portfolio'
        },
        {
          title: 'Certificate',
          description: 'Receive a verified certificate upon completing the program'
        },
        {
          title: 'Community Access',
          description: 'Join our private community of learners and alumni'
        },
        {
          title: 'Lifetime Updates',
          description: 'Get all future course updates and new modules at no extra cost'
        }
      ]
    },
    curriculum: {
      title: 'Program Curriculum'
    },
    cta: {
      goToDashboard: 'Go to Dashboard',
      backToHome: 'Back to Home',
      enrollNow: 'Enroll Now',
      viewCurriculum: 'View Full Curriculum'
    },
    moduleCards: {
      module1: {
        badge: 'Module 1 FREE',
        title: 'Product Vision & Skill Assessment',
        details: `✓ Understanding your idea
✓ Clarifying what you want to build
✓ Personalized AI analysis of your product idea
✓ Product validation & "is this even useful?"

(Complete this module for free on the Free Module page)`
      },
      module2: {
        badge: 'Module 2',
        title: 'Tools, Environment & Setup',
        details: `✓ Choosing the right tech stack
✓ Mobile vs Web vs Cross-platform
✓ When to Choose What
✓ When to Choose React Native
✓ When React Native Is NOT the Right Choice
✓ Setting up a beginner-friendly development environment
✓ Understanding how tools work together
✓ UI/UX Basics for AI Developers — understanding design thinking without becoming a designer

"Feeling confident instead of overwhelmed"

Module Description

Before building products, it's important to understand what tools you actually need — and why.

In this module, you stop blindly following trends and start choosing tools intentionally.
You will learn:

• how to think about tech stack choices
• when to build mobile, web, or cross-platform
• when React Native is the right choice — and when it isn't

We will set up a beginner-friendly development environment and connect all tools into one clear workflow.

This module also covers essential design principles for developers.
For a deep dive into UI/UX, Figma, and design systems, check out our dedicated UI/UX Basics course.`
      },
      module3: {
        badge: 'Module 3',
        title: 'AI as Your Development Partner',
        details: `✓ How to work with AI effectively
✓ How to choose the right AI mode depending on the task
✓ How to explain tasks clearly and get useful, predictable results
✓ Best practices for collaborating with AI during development

"AI is powerful — but only when you know what to ask and how to ask."

Module Description

AI is not a replacement for thinking — it's a tool that writes code for you. In this module, you learn how to work with AI as a partner, not as a black box.

You will understand:
• How to explain tasks clearly
• How to choose the right AI mode for different problems
• How to get predictable, useful results instead of random answers

We focus on collaboration, not delegation. You stay in control of decisions, structure, and architecture — AI helps you move faster and reduce routine. This module teaches you how to use AI confidently and intentionally during real product development.`
      },
      module4: {
        badge: 'Module 4',
        title: 'Architecture & System Design',
        details: `✓ Frontend vs Backend — explained simply
✓ What does Frontend consist of?
✓ What does Backend consist of?
✓ Frontend Structure - files and folders
✓ Backend Structure - files and folders
✓ Why Supabase? (and alternatives: Firebase, custom backend)
✓ Supabase as a Backend Platform
✓ Supabase Structure - files and folders
✓ How Supabase Fits into the Data Flow
✓ Supabase "Directly" vs "Through Edge Functions"
✓ How Data Flows Inside an App or Website (Final Overview)

"This module gives you a mental map. Tools might change in the future. Architecture thinking stays."

Module Description

In this module, everything finally comes together.
You stop seeing an app as a set of random screens and start seeing it as a system.

We break down architecture into simple, understandable parts:
• frontend and backend
• design as part of system thinking
• responsibilities and data flow
• files, folders, and structure

You will learn how a real application is organized:
• where code lives
• how different layers communicate
• how data flows from the user to the system and back

We also introduce Supabase as a backend platform and show how it fits naturally into modern app architecture.

This module gives you a mental map. Tools may change in the future — architectural thinking stays.

Bonus: practical architecture rules for working with AI developers.
Once you see the system, development becomes calm.`
      },
      module5: {
        badge: 'Module 5',
        title: 'Architecture for Websites',
        details: `✓ App vs Website — what changes and what stays the same
✓ Tech stack perspective (tools, not architecture)
✓ Website folder structure — explained simply
✓ Routing & URLs — the biggest difference
✓ App flow vs Web flow (simple comparison)
✓ Data fetching in websites — when and where
✓ Same backend, different frontend

"If you understand app architecture, web architecture becomes a translation — not a new subject"

Bonus: Rules for AI Developers

Module Description

In this module, you learn how architectural thinking transfers from apps to websites.

You will see that web architecture is not a new subject — it's the same system thinking in a different shape.

We compare apps and websites side by side:
• what stays the same
• what changes
• and why URLs become a core part of web architecture

You will understand:
• how website folder structures are organized
• how routing and pages work differently from app navigation
• when and where data is fetched on the web

Most importantly, you will see that the backend does not change.
The same backend can power both apps and websites — only the frontend shape is different.

If you understand app architecture, web architecture becomes a translation — not a new subject.

Bonus: practical architecture rules for working with AI developers on web projects.`
      },
      module6: {
        badge: 'Module 6',
        title: 'From Prototype to Product',
        details: `✓ Draft vs Final Product — what's the difference
✓ When to refactor vs when to rebuild from scratch
✓ Cleaning up project structure (frontend & backend)
✓ Placing code into the correct folders
✓ Removing duplication and accidental complexity
✓ Connecting frontend, backend, and data flow cleanly
✓ Preparing the project for growth and real use
✓ Performance Basics: optimizing images, videos & components

"This is the moment where experimentation ends and intentional building begins"

Module Description

In previous modules, you worked with ideas, drafts, and experiments.
You learned how to think, how to design systems, and how to work with AI.

In this module, we move from learning mode to building mode.
You will take your project and:
• clean it up
• restructure it properly
• or rebuild it from scratch if that's the better decision

This module teaches you how real products are created — not as demos, but as systems that can be maintained, extended, and shipped.

Sometimes the best refactor is a clean start.

This module shows you how to make that decision confidently.`
      },
      module7: {
        badge: 'Module 7',
        title: 'Product Readiness',
        details: `Block 1 - Security Basics for Product Builders
Block 2 - Integrating Real-World Services

✓ What sensitive data is
✓ Where API keys should live
✓ What should never be stored in frontend code
✓ Common beginner mistakes that lead to problems
✓ How API integrations work conceptually
✓ Where integration logic belongs in the system
✓ How frontend, backend, and external services communicate

"APIs are bridges, not shortcuts. Before launching a product, you must understand responsibility."

Module Description

Block 1 - Security Basics for Product Builders

Before launching a product, it's important to understand what security actually means.
This module is not about deep security engineering. It's about awareness.

You will learn:
• what sensitive data is
• where API keys should live
• what should never be stored in frontend code
• common beginner mistakes that lead to problems

This module helps you avoid mistakes that are easy to make — and hard to fix later.
Security is not fear. It's responsibility.

Block 2 - Integrating Real-World Services

Real products rarely live alone.
They connect to external services:
• payments
• emails
• maps
• analytics
• third-party APIs

In this module, you learn:
• how API integrations work conceptually
• where this logic belongs in the system
• how frontend, backend, and external services communicate

This module gives you a clear mental model for connecting real-world services safely and cleanly.`
      },
      module8: {
        badge: 'Module 8',
        title: 'Launching Your Web Product',
        details: `✓ Web hosting — explained simply
✓ What a domain is and how it works
✓ Choosing a hosting platform (Vercel, others)
✓ Deploying a web frontend
✓ Connecting backend and database in production
✓ Environment variables in real projects
✓ Common mistakes when launching websites
✓ What "live" really means

"A website is real only when it has a URL and real users can open it."

Module Description

In this module, your product goes live.
You already have a clean, structured project. Now you learn how to make it accessible to real users on the internet.

We focus on web product launch, step by step:
• hosting and domains
• deployment platforms
• environments and configuration
• connecting frontend, backend, and database in production

You will understand not just how to deploy, but why each step exists and how to choose the right setup.

This module removes the fear of "going live" and turns deployment into a clear, repeatable process.`
      },
      module9: {
        badge: 'Module 9',
        title: 'Launching Your Mobile App',
        details: `✓ Mobile app lifecycle — explained simply
✓ Build vs Release — what's the difference
✓ Preparing your app for launch
✓ App Store & Google Play — how they work
✓ Certificates, builds, and versions (high level)
✓ Publishing and updates
✓ Common beginner mistakes when launching apps
✓ What happens after launch

"A mobile app is real only when it lives on users' devices."

Module Description

Launching a mobile app is different from launching a website.

In this module, you learn how mobile apps move from your computer to users' phones.
We break down the process into simple, understandable steps:
• building app versions
• preparing app metadata
• understanding app stores
• publishing and updates

You will see how app stores work, what they check, and why these rules exist.

This module helps you understand the full lifecycle of a mobile app — from development to distribution.`
      }
    }
  },
  uk: {
    hero: {
      badge: 'Повний курс',
      title: 'Повна програма',
      subtitle: 'Опануйте AI-розробку від нуля до запуску продукту'
    },
    pricing: {
      originalPrice: '$1200',
      currentPrice: '$900',
      discount: 'Зекономте $300',
      button: 'Перейти до оплати',
      secure: '🔒 Безпечна оплата через Stripe та monobank',
      noAccount: 'Обліковий запис не потрібен',
      payWith: 'Оплата карткою (Visa/Mastercard) або monobank',
      currency: 'Конвертація валюти відображається при оформленні.'
    },
    content: {
      badge: '🎓 Повний навчальний шлях',
      description: 'Отримайте необмежений доступ до всіх модулів, ексклюзивного контенту та персонального менторства. Повний шлях від новачка до впевненого AI-розробника продуктів.'
    },
    features: {
      title: 'Що включено',
      items: [
        {
          title: 'Всі модулі розблоковано',
          description: 'Повний доступ до кожного модуля програми, включаючи майбутні доповнення'
        },
        {
          title: 'Персональне менторство',
          description: 'Індивідуальні сесії з досвідченими менторами для супроводу вашого навчання'
        },
        {
          title: 'Реальні проекти',
          description: 'Створюйте справжні продукти, які можна показати у портфоліо'
        },
        {
          title: 'Сертифікат',
          description: 'Отримайте підтверджений сертифікат після завершення програми'
        },
        {
          title: 'Доступ до спільноти',
          description: 'Приєднуйтесь до нашої приватної спільноти учнів та випускників'
        },
        {
          title: 'Довічні оновлення',
          description: 'Отримуйте всі майбутні оновлення курсу та нові модулі безкоштовно'
        }
      ]
    },
    curriculum: {
      title: 'Навчальна програма'
    },
    cta: {
      goToDashboard: 'Перейти до панелі',
      backToHome: 'На головну',
      enrollNow: 'Записатися зараз',
      viewCurriculum: 'Переглянути програму'
    },
    moduleCards: {
      module1: {
        badge: 'Модуль 1 БЕЗКОШТОВНО',
        title: 'Бачення продукту та оцінка навичок',
        details: `✓ Розуміння вашої ідеї
✓ Уточнення того, що ви хочете створити
✓ Персоналізований AI-аналіз вашої ідеї продукту
✓ Валідація продукту та "чи це взагалі корисно?"

(Пройдіть цей модуль безкоштовно на сторінці Free Module)`
      },
      module2: {
        badge: 'Модуль 2',
        title: 'Інструменти, середовище та налаштування',
        details: `✓ Вибір правильного технологічного стеку
✓ Мобільний vs Веб vs Крос-платформний
✓ Коли обирати що
✓ Коли обирати React Native
✓ Коли React Native НЕ є правильним вибором
✓ Налаштування зручного середовища розробки для початківців
✓ Розуміння як інструменти працюють разом
✓ Основи UI/UX для AI-розробників — розуміння дизайн-мислення без необхідності ставати дизайнером

"Відчуття впевненості замість перевантаження"

Опис модуля

Перед створенням продуктів важливо зрозуміти, які інструменти вам насправді потрібні — і чому.

У цьому модулі ви перестанете сліпо слідувати трендам і почнете обирати інструменти усвідомлено.
Ви навчитесь:

• як думати про вибір технологічного стеку
• коли створювати мобільний, веб або крос-платформний додаток
• коли React Native є правильним вибором — і коли ні

Ми налаштуємо зручне середовище розробки для початківців і з'єднаємо всі інструменти в один зрозумілий робочий процес.

Цей модуль також охоплює основні принципи дизайну для розробників.
Для глибокого занурення в UI/UX, Figma та дизайн-системи, перегляньте наш спеціалізований курс UI/UX Basics.`
      },
      module3: {
        badge: 'Модуль 3',
        title: 'AI як ваш партнер з розробки',
        details: `✓ Як ефективно працювати з AI
✓ Як обирати правильний режим AI залежно від завдання
✓ Як чітко пояснювати завдання і отримувати корисні, передбачувані результати
✓ Найкращі практики співпраці з AI під час розробки

"AI потужний — але тільки коли ви знаєте, що запитувати і як запитувати."

Опис модуля

AI — це не заміна мисленню — це інструмент, який пише код за вас. У цьому модулі ви навчитесь працювати з AI як з партнером, а не як з чорною скринькою.

Ви зрозумієте:
• Як чітко пояснювати завдання
• Як обирати правильний режим AI для різних проблем
• Як отримувати передбачувані, корисні результати замість випадкових відповідей

Ми фокусуємось на співпраці, а не на делегуванні. Ви залишаєтесь контролювати рішення, структуру та архітектуру — AI допомагає вам рухатись швидше та зменшує рутину. Цей модуль навчить вас використовувати AI впевнено та усвідомлено під час реальної розробки продукту.`
      },
      module4: {
        badge: 'Модуль 4',
        title: 'Архітектура та проектування систем',
        details: `✓ Frontend vs Backend — пояснено просто
✓ З чого складається Frontend?
✓ З чого складається Backend?
✓ Структура Frontend - файли та папки
✓ Структура Backend - файли та папки
✓ Чому Supabase? (та альтернативи: Firebase, власний backend)
✓ Supabase як Backend платформа
✓ Структура Supabase - файли та папки
✓ Як Supabase вписується в потік даних
✓ Supabase "Напряму" vs "Через Edge Functions"
✓ Як дані рухаються всередині додатку або сайту (Фінальний огляд)

"Цей модуль дає вам ментальну карту. Інструменти можуть змінитись у майбутньому. Архітектурне мислення залишається."

Опис модуля

У цьому модулі все нарешті з'єднується.
Ви перестаєте бачити додаток як набір випадкових екранів і починаєте бачити його як систему.

Ми розбиваємо архітектуру на прості, зрозумілі частини:
• frontend та backend
• дизайн як частина системного мислення
• відповідальності та потік даних
• файли, папки та структура

Ви дізнаєтесь, як організований справжній додаток:
• де живе код
• як різні рівні комунікують
• як дані рухаються від користувача до системи і назад

Ми також представляємо Supabase як backend платформу і показуємо, як вона природно вписується в сучасну архітектуру додатків.

Цей модуль дає вам ментальну карту. Інструменти можуть змінитись у майбутньому — архітектурне мислення залишається.

Бонус: практичні правила архітектури для роботи з AI-розробниками.
Коли ви бачите систему, розробка стає спокійною.`
      },
      module5: {
        badge: 'Модуль 5',
        title: 'Архітектура для веб-сайтів',
        details: `✓ Додаток vs Веб-сайт — що змінюється і що залишається
✓ Перспектива технологічного стеку (інструменти, не архітектура)
✓ Структура папок веб-сайту — пояснено просто
✓ Роутинг та URL — найбільша різниця
✓ Потік додатку vs Потік веб-сайту (просте порівняння)
✓ Завантаження даних на веб-сайтах — коли і де
✓ Той самий backend, інший frontend

"Якщо ви розумієте архітектуру додатків, веб-архітектура стає перекладом — а не новою темою"

Бонус: Правила для AI-розробників

Опис модуля

У цьому модулі ви дізнаєтесь, як архітектурне мислення переноситься з додатків на веб-сайти.

Ви побачите, що веб-архітектура — це не нова тема — це те саме системне мислення в іншій формі.

Ми порівнюємо додатки та веб-сайти пліч-о-пліч:
• що залишається незмінним
• що змінюється
• і чому URL стають ключовою частиною веб-архітектури

Ви зрозумієте:
• як організовані структури папок веб-сайтів
• як роутинг і сторінки працюють інакше від навігації в додатках
• коли і де завантажуються дані на вебі

Найважливіше — ви побачите, що backend не змінюється.
Той самий backend може обслуговувати і додатки, і веб-сайти — змінюється лише форма frontend.

Якщо ви розумієте архітектуру додатків, веб-архітектура стає перекладом — а не новою темою.

Бонус: практичні правила архітектури для роботи з AI-розробниками над веб-проектами.`
      },
      module6: {
        badge: 'Модуль 6',
        title: 'Від прототипу до продукту',
        details: `✓ Чернетка vs Фінальний продукт — яка різниця
✓ Коли рефакторити vs коли перебудовувати з нуля
✓ Очищення структури проекту (frontend та backend)
✓ Розміщення коду в правильних папках
✓ Видалення дублювання та випадкової складності
✓ Чисте з'єднання frontend, backend та потоку даних
✓ Підготовка проекту до зростання та реального використання
✓ Основи продуктивності: оптимізація зображень, відео та компонентів

"Це момент, коли експериментування закінчується і починається усвідомлене будівництво"

Опис модуля

У попередніх модулях ви працювали з ідеями, чернетками та експериментами.
Ви навчились думати, проектувати системи та працювати з AI.

У цьому модулі ми переходимо від режиму навчання до режиму будівництва.
Ви візьмете свій проект і:
• очистите його
• правильно реструктуруєте
• або перебудуєте з нуля, якщо це краще рішення

Цей модуль навчає, як створюються справжні продукти — не як демо, а як системи, які можна підтримувати, розширювати та випускати.

Іноді найкращий рефакторинг — це чистий старт.

Цей модуль показує, як приймати це рішення впевнено.`
      },
      module7: {
        badge: 'Модуль 7',
        title: 'Готовність продукту',
        details: `Блок 1 - Основи безпеки для творців продуктів
Блок 2 - Інтеграція реальних сервісів

✓ Що таке чутливі дані
✓ Де мають зберігатися API ключі
✓ Що ніколи не має зберігатися у frontend коді
✓ Типові помилки початківців, які призводять до проблем
✓ Як API інтеграції працюють концептуально
✓ Де логіка інтеграцій належить у системі
✓ Як frontend, backend та зовнішні сервіси комунікують

"API — це мости, не скорочення. Перед запуском продукту потрібно розуміти відповідальність."

Опис модуля

Блок 1 - Основи безпеки для творців продуктів

Перед запуском продукту важливо зрозуміти, що насправді означає безпека.
Цей модуль не про глибоку інженерію безпеки. Він про усвідомлення.

Ви дізнаєтесь:
• що таке чутливі дані
• де мають зберігатися API ключі
• що ніколи не має зберігатися у frontend коді
• типові помилки початківців, які призводять до проблем

Цей модуль допомагає уникнути помилок, які легко зробити — і важко виправити пізніше.
Безпека — це не страх. Це відповідальність.

Блок 2 - Інтеграція реальних сервісів

Справжні продукти рідко живуть самі.
Вони з'єднуються із зовнішніми сервісами:
• платежі
• електронна пошта
• карти
• аналітика
• сторонні API

У цьому модулі ви дізнаєтесь:
• як API інтеграції працюють концептуально
• де ця логіка належить у системі
• як frontend, backend та зовнішні сервіси комунікують

Цей модуль дає вам чітку ментальну модель для безпечного та чистого з'єднання реальних сервісів.`
      },
      module8: {
        badge: 'Модуль 8',
        title: 'Запуск вашого веб-продукту',
        details: `✓ Веб-хостинг — пояснено просто
✓ Що таке домен і як він працює
✓ Вибір платформи хостингу (Vercel, інші)
✓ Деплой веб-frontend
✓ З'єднання backend та бази даних у продакшені
✓ Змінні середовища в реальних проектах
✓ Типові помилки при запуску веб-сайтів
✓ Що насправді означає "в ефірі"

"Веб-сайт реальний лише тоді, коли має URL і справжні користувачі можуть його відкрити."

Опис модуля

У цьому модулі ваш продукт стає живим.
Ви вже маєте чистий, структурований проект. Тепер ви дізнаєтесь, як зробити його доступним для реальних користувачів в інтернеті.

Ми фокусуємось на запуску веб-продукту крок за кроком:
• хостинг та домени
• платформи деплою
• середовища та конфігурація
• з'єднання frontend, backend та бази даних у продакшені

Ви зрозумієте не лише як деплоїти, але й чому існує кожен крок і як обрати правильні налаштування.

Цей модуль знімає страх "виходу в ефір" і перетворює деплой на зрозумілий, повторюваний процес.`
      },
      module9: {
        badge: 'Модуль 9',
        title: 'Запуск вашого мобільного додатку',
        details: `✓ Життєвий цикл мобільного додатку — пояснено просто
✓ Build vs Release — яка різниця
✓ Підготовка додатку до запуску
✓ App Store та Google Play — як вони працюють
✓ Сертифікати, збірки та версії (високий рівень)
✓ Публікація та оновлення
✓ Типові помилки початківців при запуску додатків
✓ Що відбувається після запуску

"Мобільний додаток реальний лише тоді, коли живе на пристроях користувачів."

Опис модуля

Запуск мобільного додатку відрізняється від запуску веб-сайту.

У цьому модулі ви дізнаєтесь, як мобільні додатки переміщуються з вашого комп'ютера на телефони користувачів.
Ми розбиваємо процес на прості, зрозумілі кроки:
• створення версій додатку
• підготовка метаданих додатку
• розуміння магазинів додатків
• публікація та оновлення

Ви побачите, як працюють магазини додатків, що вони перевіряють і чому існують ці правила.

Цей модуль допомагає зрозуміти повний життєвий цикл мобільного додатку — від розробки до дистрибуції.`
      }
    }
  }
};
